import { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, TrendingUp, Globe, Clock, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AnalyticsManager({ headers }) {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('30');

    useEffect(() => {
        fetchAnalytics();
    }, [period]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/analytics/detailed?days=${period}`, { headers });
            setAnalytics(res.data);
        } catch (error) {
            toast.error('Failed to fetch analytics');
        }
        setLoading(false);
    };

    const maxDailyVisits = analytics?.daily_visits ? Math.max(...analytics.daily_visits.map(d => d.visits), 1) : 1;
    const maxPageVisits = analytics?.page_breakdown ? Math.max(...analytics.page_breakdown.map(p => p.visits), 1) : 1;
    const maxHourlyVisits = analytics?.hourly_distribution ? Math.max(...analytics.hourly_distribution.map(h => h.visits), 1) : 1;

    return (
        <div className="space-y-6" data-testid="analytics-manager">
            {/* Header */}
            <div className="retro-card p-6">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h2 className="text-lg uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                            Analytics Dashboard
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Detailed visitor insights for your site
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <select 
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                            className="rounded-none border-2 border-border bg-background px-3 py-2 text-xs uppercase tracking-widest"
                            data-testid="analytics-period-select"
                        >
                            <option value="7">Last 7 days</option>
                            <option value="14">Last 14 days</option>
                            <option value="30">Last 30 days</option>
                            <option value="90">Last 90 days</option>
                        </select>
                        <Button 
                            variant="outline" 
                            size="icon"
                            onClick={fetchAnalytics}
                            disabled={loading}
                            className="rounded-none border-2"
                            data-testid="refresh-analytics-btn"
                        >
                            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent animate-spin" />
                    </div>
                ) : analytics ? (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-muted/30 border-2 border-border">
                            <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Visits</p>
                            <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'Silkscreen', cursive" }}>{analytics.total_visits}</p>
                        </div>
                        <div className="p-4 bg-muted/30 border-2 border-border">
                            <p className="text-xs uppercase tracking-widest text-muted-foreground">Unique Visitors</p>
                            <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'Silkscreen', cursive" }}>{analytics.unique_visitors}</p>
                        </div>
                        <div className="p-4 bg-muted/30 border-2 border-border">
                            <p className="text-xs uppercase tracking-widest text-muted-foreground">Avg Daily</p>
                            <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'Silkscreen', cursive" }}>{analytics.avg_daily_visits}</p>
                        </div>
                        <div className="p-4 bg-muted/30 border-2 border-border">
                            <p className="text-xs uppercase tracking-widest text-muted-foreground">Peak Hour</p>
                            <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'Silkscreen', cursive" }}>{analytics.peak_hour}:00</p>
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Daily Visits Chart */}
            {analytics && (
                <div className="retro-card p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="h-5 w-5 text-primary" />
                        <h3 className="text-sm uppercase tracking-widest font-bold">Visits Over Time</h3>
                    </div>
                    <div className="h-48 flex items-end gap-1" data-testid="daily-visits-chart">
                        {analytics.daily_visits?.map((day, i) => (
                            <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group">
                                <div className="relative w-full flex justify-center mb-1">
                                    <span className="absolute -top-6 text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity bg-background px-1">
                                        {day.visits}
                                    </span>
                                </div>
                                <div 
                                    className="w-full bg-primary/80 hover:bg-primary transition-colors"
                                    style={{ 
                                        height: `${Math.max((day.visits / maxDailyVisits) * 100, 2)}%`,
                                        minHeight: '4px'
                                    }}
                                    title={`${day.date}: ${day.visits} visits`}
                                />
                                <span className="text-[8px] text-muted-foreground mt-1 rotate-45 origin-left whitespace-nowrap">
                                    {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Page Breakdown */}
                {analytics && (
                    <div className="retro-card p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Globe className="h-5 w-5 text-primary" />
                            <h3 className="text-sm uppercase tracking-widest font-bold">Page Views</h3>
                        </div>
                        <div className="space-y-3" data-testid="page-breakdown-chart">
                            {analytics.page_breakdown?.slice(0, 8).map((page, i) => (
                                <div key={i} className="space-y-1">
                                    <div className="flex justify-between text-xs">
                                        <span className="truncate max-w-[200px]" title={page.page}>{page.page === '/' ? 'Home' : page.page}</span>
                                        <span className="text-muted-foreground">{page.visits}</span>
                                    </div>
                                    <div className="h-2 bg-muted w-full">
                                        <div 
                                            className="h-full bg-primary"
                                            style={{ width: `${(page.visits / maxPageVisits) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                            {(!analytics.page_breakdown || analytics.page_breakdown.length === 0) && (
                                <p className="text-sm text-muted-foreground text-center py-4">No page data available</p>
                            )}
                        </div>
                    </div>
                )}

                {/* Hourly Distribution */}
                {analytics && (
                    <div className="retro-card p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Clock className="h-5 w-5 text-primary" />
                            <h3 className="text-sm uppercase tracking-widest font-bold">Hourly Activity</h3>
                        </div>
                        <div className="h-32 flex items-end gap-0.5" data-testid="hourly-distribution-chart">
                            {analytics.hourly_distribution?.map((hour, i) => (
                                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group">
                                    <div 
                                        className="w-full bg-primary/60 hover:bg-primary transition-colors"
                                        style={{ 
                                            height: `${Math.max((hour.visits / maxHourlyVisits) * 100, 2)}%`,
                                            minHeight: '2px'
                                        }}
                                        title={`${hour.hour}:00 - ${hour.visits} visits`}
                                    />
                                    {i % 4 === 0 && (
                                        <span className="text-[8px] text-muted-foreground mt-1">{hour.hour}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-2 text-center">Hours (UTC)</p>
                    </div>
                )}
            </div>

            {/* Device/Browser Breakdown */}
            {analytics && analytics.browser_breakdown && (
                <div className="retro-card p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <BarChart3 className="h-5 w-5 text-primary" />
                        <h3 className="text-sm uppercase tracking-widest font-bold">Browser Distribution</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="browser-breakdown">
                        {analytics.browser_breakdown.slice(0, 8).map((browser, i) => (
                            <div key={i} className="p-3 bg-muted/30 border-2 border-border text-center">
                                <p className="text-lg font-bold" style={{ fontFamily: "'Silkscreen', cursive" }}>{browser.percentage}%</p>
                                <p className="text-xs text-muted-foreground uppercase tracking-widest truncate" title={browser.browser}>{browser.browser}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
